import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, CircularProgress, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Button, Popover, Switch, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    BarElement,
    BarController,
    Filler,
    Chart
} from 'chart.js';
import 'chartjs-adapter-moment';
import moment from 'moment-timezone';
import 'moment/locale/nl'; // Importeer Nederlandse locale
import { nl } from 'date-fns/locale';
import { useApi } from '../hooks/useApi';
import { format, parseISO, startOfDay, differenceInMinutes, addDays, subDays, addHours } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

// Utility functies voor tijdconversie met moment.js
const UTC2CET = (date) => {
    return moment(date).tz('Europe/Amsterdam').toDate();
};

const CET2UTC = (date) => {
    return moment(date).tz('Europe/Amsterdam').utc().toDate();
};

// Registreer de benodigde Chart.js componenten
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    BarElement,
    BarController,
    Filler
);

// Configureer moment voor Chart.js en zet Nederlandse locale
moment.locale('nl');
moment.tz.setDefault('Europe/Amsterdam');

const Planning = () => {
    const { t } = useTranslation();
    const [scheduleData, setScheduleData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
    const [selectedQuarters, setSelectedQuarters] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectionMode, setSelectionMode] = useState(null);
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [dragState, setDragState] = useState({
        isActive: false,
        start: null,
        end: null,
        hasMoved: false
    });
    const chartRef = useRef(null);
    const { get, post, delete: deleteSchedule } = useApi();
    const [showModeOptions, setShowModeOptions] = useState(false);
    const [showEmissionFactor, setShowEmissionFactor] = useState(false);
    const [showHydrogenPrice, setShowHydrogenPrice] = useState(false);
    const [configSettings, setConfigSettings] = useState({});
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [electricitySettings, setElectricitySettings] = useState({ show_all_in: true });
    const [hydrogenDisplaySettings, setHydrogenDisplaySettings] = useState({ 
        hydrogen_type: 'green', 
        show_retail: true 
    });

    // Haal electricity price settings op
    useEffect(() => {
        const fetchElectricitySettings = async () => {
            try {
                const response = await get('/electricity-price/settings');
                if (response.data) {
                    setElectricitySettings(response.data);
                }
            } catch (err) {
                console.error('Fout bij ophalen electricity price settings:', err);
                // Gebruik default settings bij fout
                setElectricitySettings({ show_all_in: true });
            }
        };

        fetchElectricitySettings();
    }, []);

    // Haal hydrogen display settings op
    useEffect(() => {
        const fetchHydrogenDisplaySettings = async () => {
            try {
                const response = await get('/hydrogen-price/display-settings');
                if (response.data) {
                    setHydrogenDisplaySettings(response.data);
                }
            } catch (err) {
                console.error('Fout bij ophalen hydrogen display settings:', err);
                // Gebruik default settings bij fout
                setHydrogenDisplaySettings({ hydrogen_type: 'green', show_retail: true });
            }
        };

        fetchHydrogenDisplaySettings();
    }, []);

    // Haal configuratie op bij het laden van de component
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchConfig = async () => {
            try {
                const response = await get('/config', { signal: controller.signal });
                if (!mounted) return;
                if (response.data && response.data.configuration) {
                    const configObj = {};
                    response.data.configuration.forEach(item => {
                        configObj[item.parameter] = parseFloat(item.value);
                    });
                    setConfigSettings(configObj);
                }
            } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
                handleError(err);
            }
        };

        fetchConfig();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, []);

    // De waterstofprijs wordt nu gefilterd op basis van display settings
    // Functie om de juiste waterstofprijs kolom te bepalen
    function getHydrogenPriceField() {
        const typePrefix = `hydrogen_${hydrogenDisplaySettings.hydrogen_type}`;
        const suffix = hydrogenDisplaySettings.show_retail ? '_retail_price' : '_price';
        return `${typePrefix}${suffix}`;
    }

    // Functie om waterstofprijs te halen uit data object
    function getHydrogenPrice(data) {
        const field = getHydrogenPriceField();
        return data[field];
    }

    // Functie om het juiste stroomprijs label te bepalen
    function getElectricityPriceLabel() {
        return electricitySettings.show_all_in 
            ? 'All-in Stroomprijs (€/kWh)'
            : 'Kale Stroomprijs (€/kWh)';
    }

    // Functie om het juiste waterstofprijs label te bepalen
    function getHydrogenPriceLabel() {
        const typeMapping = {
            'green': 'Groene',
            'grey': 'Grijze', 
            'blue': 'Blauwe'
        };
        const typeName = typeMapping[hydrogenDisplaySettings.hydrogen_type] || 'Groene';
        const priceType = hydrogenDisplaySettings.show_retail ? 'Retail' : 'Markt';
        return `${typeName} Waterstof ${priceType} Prijs (€/kg)`;
    }

    // Functie om de juiste prijs te bepalen (kale prijs of all-in prijs)
    const getPrice = (data) => {
        if (electricitySettings.show_all_in) {
            // Gebruik all-in prijs direct uit database als beschikbaar
            if (data.price_all_in !== null && data.price_all_in !== undefined) {
                return data.price_all_in;
            }
            // Fallback: bereken all-in prijs als database veld leeg is
            const netbeheerderTarief = data.price_netbeheerder_tarief || electricitySettings.netbeheerder_tarief || 0.12;
            const leverancierTarief = data.price_leverancier_tarief || electricitySettings.leverancier_tarief || 0.08;
            return data.price + netbeheerderTarief + leverancierTarief;
        } else {
            // Gebruik kale prijs (originele price veld)
            return data.price;
        }
    };

    // Functie om de gemiddelde prijs, emissiefactor en waterstofprijs voor een periode te berekenen
    const calculateAverages = (start, end) => {
        const relevantData = scheduleData.filter(data => {
            const time = parseISO(data.validfrom).getTime();
            return time >= start && time < end;
        });

        if (relevantData.length === 0) return { price: 0, emissionFactor: 0, hydrogenPrice: 0 };

        const totalPrice = relevantData.reduce((sum, data) => sum + getPrice(data), 0);
        const totalEmissionFactor = relevantData.reduce((sum, data) => sum + data.emissionfactor, 0);
        
        // Filter alleen data met waterstofprijs voor gemiddelde berekening
        const hydrogenData = relevantData.filter(data => {
            const hydrogenPrice = getHydrogenPrice(data);
            return hydrogenPrice !== null && hydrogenPrice !== undefined;
        });
        const totalHydrogenPrice = hydrogenData.reduce((sum, data) => sum + getHydrogenPrice(data), 0);

        return {
            price: totalPrice / relevantData.length,
            emissionFactor: totalEmissionFactor / relevantData.length,
            hydrogenPrice: hydrogenData.length > 0 ? totalHydrogenPrice / hydrogenData.length : 0
        };
    };

    // Functie om de aan-periodes te analyseren
    const analyzeOnPeriods = () => {
        if (!scheduleData.length) return [];

        // Sorteer de blokken op starttijd zodat ze altijd chronologisch zijn.
        const sortedData = [...scheduleData].sort(
            (a, b) => parseISO(a.validfrom).getTime() - parseISO(b.validfrom).getTime()
        );

        // Filter alleen de blokken die binnen de geselecteerde dag vallen om dubbele periodes te voorkomen
        const dayStart = moment(selectedDate).tz('Europe/Amsterdam').startOf('day').valueOf();
        const dayEnd = moment(selectedDate).tz('Europe/Amsterdam').endOf('day').valueOf();

        const filteredData = sortedData.filter(block => {
            const blockStart = parseISO(block.validfrom).getTime();
            return blockStart >= dayStart && blockStart <= dayEnd;
        });

        const periods = [];
        let currentPeriod = null;

        filteredData.forEach((block) => {
            const isOn = block.manual === true || (block.manual === null && block.automatic);
            const status = block.manual === true ? 'Override actief' : 'Autopilot';

            if (isOn) {
                if (!currentPeriod) {
                    // Start nieuwe periode
                    currentPeriod = {
                        start: parseISO(block.validfrom).getTime(),
                        end: parseISO(block.validto).getTime(),
                        status: status
                    };
                } else if (currentPeriod.status === status) {
                    // Verleng bestaande periode
                    currentPeriod.end = parseISO(block.validto).getTime();
                } else {
                    // Einde van periode, start nieuwe
                    periods.push(currentPeriod);
                    currentPeriod = {
                        start: parseISO(block.validfrom).getTime(),
                        end: parseISO(block.validto).getTime(),
                        status: status
                    };
                }
            } else if (currentPeriod) {
                // Einde van periode
                periods.push(currentPeriod);
                currentPeriod = null;
            }
        });

        // Voeg laatste periode toe als die er is
        if (currentPeriod) {
            periods.push(currentPeriod);
        }

        // Bereken verbruik en kosten voor elke periode
        return periods.map(period => {
            const duration = differenceInMinutes(period.end, period.start);
            const hours = duration / 60; // Omrekenen naar uren voor berekening
            const powerConsumption = Number.isFinite(configSettings.power) ? configSettings.power : 1000; // kW (default 1000 kW)
            const efficiency = Number.isFinite(configSettings.efficiency) ? configSettings.efficiency / 100 : 0.61; // efficiëntie als decimaal (default 61%)
            const energyPerKgKwh = Number.isFinite(configSettings.energy_per_kg_kwh) ? configSettings.energy_per_kg_kwh : 13.3333; // kWh per kg H₂ (default 13.3333)
            const averages = calculateAverages(period.start, period.end);
            
            // Waterstof berekeningen
            const totalConsumption = powerConsumption * hours; // kWh
            const energyForHydrogen = totalConsumption * efficiency; // kWh effectief gebruikt voor waterstof
            const hydrogenProduced = energyForHydrogen / energyPerKgKwh; // kg waterstof
            
            // Gedetailleerde debug logging
            console.log('Waterstof berekening details:', {
                powerConsumption: `${powerConsumption} kW`,
                hours: `${hours} uur`,
                totalConsumption: `${totalConsumption} kWh`,
                efficiency: `${efficiency * 100}%`,
                energyForHydrogen: `${energyForHydrogen} kWh`,
                energyPerKgKwh: `${energyPerKgKwh} kWh/kg`,
                hydrogenProduced: `${hydrogenProduced} kg`,
                configSettings: {
                    power: configSettings.power,
                    efficiency: configSettings.efficiency,
                    energy_per_kg_kwh: configSettings.energy_per_kg_kwh
                }
            });
            
            return {
                ...period,
                duration,
                hours,
                powerConsumption,
                totalConsumption,
                averagePrice: averages.price,
                averageEmissionFactor: averages.emissionFactor,
                averageHydrogenPrice: averages.hydrogenPrice,
                totalCost: totalConsumption * averages.price, // €
                hydrogenProduced,
                hydrogenCost: hydrogenProduced * averages.hydrogenPrice // € kosten voor waterstof
            };
        });
    };

    // Memoize onPeriods om onnodige herberekeningen te voorkomen
    const onPeriods = useMemo(() => analyzeOnPeriods(), [scheduleData, configSettings, selectedDate]);

    const handleError = useCallback((error) => {
        let errorMessage = t('errors.unexpected');
        let errorType = 'error';

        if (error.response) {
            // API errors
            switch (error.response.status) {
                case 400:
                    errorMessage = t('errors.badRequest', { message: error.response.data.message });
                    errorType = 'warning';
                    break;
                case 401:
                    errorMessage = t('errors.unauthorized');
                    errorType = 'error';
                    break;
                case 403:
                    errorMessage = t('errors.forbidden');
                    errorType = 'error';
                    break;
                case 404:
                    errorMessage = t('errors.notFound');
                    errorType = 'warning';
                    break;
                case 429:
                    errorMessage = t('errors.tooManyRequests');
                    errorType = 'warning';
                    break;
                case 500:
                    errorMessage = t('errors.serverError');
                    errorType = 'error';
                    break;
                default:
                    errorMessage = t('errors.apiError', { message: error.response.data.message });
                    errorType = 'error';
            }
        } else if (error.request) {
            // Network errors
            errorMessage = t('errors.noConnection');
            errorType = 'error';
        } else if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            // Aborted requests
            return; // Geen error tonen voor geannuleerde requests
        }

        setError({
            message: errorMessage,
            type: errorType,
            details: error.response?.data?.details || null
        });
        console.error('Error:', error);
    }, [t]);

    // Effect voor het ophalen van data
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchData = async () => {
            if (!mounted) return;
            
            try {
                setLoading(true);
                
                // Converteer de geselecteerde datum naar UTC voor de API call
                const cetStart = UTC2CET(selectedDate);
                const validfrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
                const validto = format(CET2UTC(addDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
                
                console.log('Fetching data for:', {
                    selectedDate: selectedDate,
                    cetStart: cetStart,
                    validfrom: validfrom,
                    validto: validto
                });
                
                const scheduleResponse = await get(`/schedule?validfrom=${validfrom}&validto=${validto}`, {
                    signal: controller.signal
                });

                if (!mounted) return;

                if (scheduleResponse.data?.blocks?.length > 0) {
                    const convertedBlocks = scheduleResponse.data.blocks.map(block => ({
                        ...block,
                        validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                        validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                    }));
                    setScheduleData(convertedBlocks);
                } else {
                    setScheduleData([]);
                }
            } catch (err) {
                if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
                handleError(err);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [selectedDate]);

    // Effect om de huidige tijd bij te werken
    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            // Alleen updaten als we de huidige dag bekijken
            if (startOfDay(selectedDate).getTime() === startOfDay(now).getTime()) {
                setCurrentTime(now);
            }
        }, 1000); // Update elke seconde

        return () => clearInterval(timer);
    }, [selectedDate]);

    // Effect om selectionMode te updaten wanneer showModeOptions verandert
    useEffect(() => {
        if (showModeOptions && !selectionMode) {
            setSelectionMode('manual_on'); // Standaard op 'manual_on' zetten
        } else if (!showModeOptions) {
            setSelectionMode(null);
            setSelectedQuarters([]); // Reset selectie wanneer modus wordt uitgeschakeld
        }
    }, [showModeOptions, selectionMode]);

    const handlePreviousDay = () => {
        const yesterday = subDays(UTC2CET(selectedDate), 1);
        // Alleen toestaan als de datum niet voor 2024 is
        if (yesterday.getFullYear() >= 2024) {
            setSelectedDate(yesterday);
        }
    };

    const handleNextDay = () => {
        const tomorrow = addDays(UTC2CET(selectedDate), 1);
        const maxDate = addDays(UTC2CET(new Date()), 1); // Morgen
        // Alleen toestaan als de datum niet na morgen is
        if (tomorrow <= maxDate) {
            setSelectedDate(tomorrow);
        }
    };

    const handleMouseDown = (event) => {
        if (!chartRef.current || !selectionMode) return;
        
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        setDragState({
            isActive: true,
            start: { x, y },
            end: { x, y },
            hasMoved: false
        });
    };

    const handleMouseMove = (event) => {
        if (!dragState.isActive || !chartRef.current) return;
        
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Als we bewegen, markeer het als een sleep
        const hasMoved = Math.abs(x - dragState.start.x) > 5 || Math.abs(y - dragState.start.y) > 5;
        
        setDragState(prev => ({
            ...prev,
            end: { x, y },
            hasMoved: prev.hasMoved || hasMoved
        }));
    };

    const handleMouseUp = () => {
        if (!dragState.isActive || !chartRef.current) return;
        
        const chart = chartRef.current;
        const xScale = chart.scales.x;
        
        // Bereken de kwartier breedte (15 minuten)
        const quarterWidth = 15 * 60 * 1000;
        
        if (!dragState.hasMoved) {
            // Als het een klik was, simpele toggle
            const x = dragState.start.x;
            const xValue = xScale.getValueForPixel(x);
            const adjustedXValue = xValue + (quarterWidth / 2);
            
            const clickedQuarter = scheduleData.find(schedule => {
                const quarterStart = parseISO(schedule.validfrom).getTime();
                const quarterEnd = parseISO(schedule.validto).getTime();
                return adjustedXValue >= quarterStart && adjustedXValue < quarterEnd;
            });

            if (clickedQuarter) {
                const quarterStart = parseISO(clickedQuarter.validfrom).getTime();
                const quarterEnd = parseISO(clickedQuarter.validto).getTime();

                setSelectedQuarters(prev => {
                    const isSelected = prev.some(q => 
                        q.start === quarterStart && 
                        q.end === quarterEnd
                    );

                    // Simpele toggle: als aan dan uit, als uit dan aan
                    if (isSelected) {
                        return prev.filter(q => 
                            q.start !== quarterStart || 
                            q.end !== quarterEnd
                        );
                    } else {
                        return [...prev, { 
                            start: quarterStart, 
                            end: quarterEnd 
                        }];
                    }
                });
            }
        } else {
            // Als het een sleep was, bepaal de nieuwe staat voor het hele bereik
            const startX = Math.min(dragState.start.x, dragState.end.x);
            const endX = Math.max(dragState.start.x, dragState.end.x);
            
            // Reken ruwe tijden om naar dichtstbijzijnde kwartiergrenzen
            const rawStartTime = xScale.getValueForPixel(startX);
            const rawEndTime = xScale.getValueForPixel(endX);

            // Start: afronden op dichtstbijzijnde kwartiergrens (>= midden wordt volgende)
            const startTime = Math.floor((rawStartTime + quarterWidth / 2) / quarterWidth) * quarterWidth;

            // Einde: altijd omhoog afronden zodat het huidige kwartier meegenomen wordt
            const endTimeRounded = Math.ceil(rawEndTime / quarterWidth) * quarterWidth;

            // Zorg dat eindtijd altijd ≥ start + 1 kwartier
            const adjustedEndTime = endTimeRounded <= startTime ? startTime + quarterWidth : endTimeRounded;
            
            const quartersInRange = scheduleData
                .filter(schedule => {
                    const quarterStart = parseISO(schedule.validfrom).getTime();
                    const quarterEnd = parseISO(schedule.validto).getTime();
                    return quarterStart >= startTime && quarterEnd <= adjustedEndTime;
                })
                .map(quarter => ({
                    start: parseISO(quarter.validfrom).getTime(),
                    end: parseISO(quarter.validto).getTime()
                }));

            setSelectedQuarters(prev => {
                // Verwijder eerst alle kwartieren uit het bereik
                const remainingQuarters = prev.filter(quarter =>
                    !quartersInRange.some(q => q.start === quarter.start && q.end === quarter.end)
                );

                // Check of alle kwartieren in het bereik al geselecteerd zijn
                const allSelected = quartersInRange.every(quarter =>
                    prev.some(q => q.start === quarter.start && q.end === quarter.end)
                );

                // Als alles al geselecteerd was: alles uit
                if (allSelected) {
                    return remainingQuarters;
                } else {
                    // Anders: alles aan
                    const combined = [...remainingQuarters, ...quartersInRange];
                    const unique = combined.filter((value, index, self) =>
                        index === self.findIndex(q => q.start === value.start && q.end === value.end)
                    );
                    return unique;
                }
            });
        }
        
        // Reset de drag state
        setDragState({
            isActive: false,
            start: null,
            end: null,
            hasMoved: false
        });
    };

    const handleMouseLeave = () => {
        // Reset de drag state als de muis de grafiek verlaat
        setDragState({
            isActive: false,
            start: null,
            end: null,
            hasMoved: false
        });
    };

    const handleClosePopover = () => {
        setPopoverAnchor(null);
    };

    const handleTouchStart = (event) => {
        if (!chartRef.current || !selectionMode) return;
        
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const touch = event.touches[0];
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        setDragState({
            isActive: true,
            start: { x, y },
            end: { x, y },
            hasMoved: false
        });
    };

    const handleTouchMove = (event) => {
        if (!dragState.isActive || !chartRef.current) return;
        
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const touch = event.touches[0];
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        // Als we bewegen, markeer het als een sleep
        const hasMoved = Math.abs(x - dragState.start.x) > 5 || Math.abs(y - dragState.start.y) > 5;
        
        setDragState(prev => ({
            ...prev,
            end: { x, y },
            hasMoved: prev.hasMoved || hasMoved
        }));
    };

    const handleTouchEnd = () => {
        if (!dragState.isActive || !chartRef.current) return;
        
        const chart = chartRef.current;
        const xScale = chart.scales.x;
        
        // Bereken de kwartier breedte (15 minuten)
        const quarterWidth = 15 * 60 * 1000;
        
        if (!dragState.hasMoved) {
            // Als het een tap was, simpele toggle
            const x = dragState.start.x;
            const xValue = xScale.getValueForPixel(x);
            const adjustedXValue = xValue + (quarterWidth / 2);
            
            const clickedQuarter = scheduleData.find(schedule => {
                const quarterStart = parseISO(schedule.validfrom).getTime();
                const quarterEnd = parseISO(schedule.validto).getTime();
                return adjustedXValue >= quarterStart && adjustedXValue < quarterEnd;
            });

            if (clickedQuarter) {
                const quarterStart = parseISO(clickedQuarter.validfrom).getTime();
                const quarterEnd = parseISO(clickedQuarter.validto).getTime();

                setSelectedQuarters(prev => {
                    const isSelected = prev.some(q => 
                        q.start === quarterStart && 
                        q.end === quarterEnd
                    );

                    // Simpele toggle: als aan dan uit, als uit dan aan
                    if (isSelected) {
                        return prev.filter(q => 
                            q.start !== quarterStart || 
                            q.end !== quarterEnd
                        );
                    } else {
                        return [...prev, { 
                            start: quarterStart, 
                            end: quarterEnd 
                        }];
                    }
                });
            }
        } else {
            // Als het een sleep was, bepaal de nieuwe staat voor het hele bereik
            const startX = Math.min(dragState.start.x, dragState.end.x);
            const endX = Math.max(dragState.start.x, dragState.end.x);
            
            // Reken ruwe tijden om naar dichtstbijzijnde kwartiergrenzen
            const rawStartTime = xScale.getValueForPixel(startX);
            const rawEndTime = xScale.getValueForPixel(endX);

            // Start: afronden op dichtstbijzijnde kwartiergrens (>= midden wordt volgende)
            const startTime = Math.floor((rawStartTime + quarterWidth / 2) / quarterWidth) * quarterWidth;

            // Einde: altijd omhoog afronden zodat het huidige kwartier meegenomen wordt
            const endTimeRounded = Math.ceil(rawEndTime / quarterWidth) * quarterWidth;

            // Zorg dat eindtijd altijd ≥ start + 1 kwartier
            const adjustedEndTime = endTimeRounded <= startTime ? startTime + quarterWidth : endTimeRounded;
            
            const quartersInRange = scheduleData
                .filter(schedule => {
                    const quarterStart = parseISO(schedule.validfrom).getTime();
                    const quarterEnd = parseISO(schedule.validto).getTime();
                    return quarterStart >= startTime && quarterEnd <= adjustedEndTime;
                })
                .map(quarter => ({
                    start: parseISO(quarter.validfrom).getTime(),
                    end: parseISO(quarter.validto).getTime()
                }));

            setSelectedQuarters(prev => {
                // Verwijder eerst alle kwartieren uit het bereik
                const remainingQuarters = prev.filter(quarter =>
                    !quartersInRange.some(q => q.start === quarter.start && q.end === quarter.end)
                );

                // Check of alle kwartieren in het bereik al geselecteerd zijn
                const allSelected = quartersInRange.every(quarter =>
                    prev.some(q => q.start === quarter.start && q.end === quarter.end)
                );

                // Als alles al geselecteerd was: alles uit
                if (allSelected) {
                    return remainingQuarters;
                } else {
                    // Anders: alles aan
                    const combined = [...remainingQuarters, ...quartersInRange];
                    const unique = combined.filter((value, index, self) =>
                        index === self.findIndex(q => q.start === value.start && q.end === value.end)
                    );
                    return unique;
                }
            });
        }
        
        // Reset de drag state
        setDragState({
            isActive: false,
            start: null,
            end: null,
            hasMoved: false
        });
    };

    const handleTouchCancel = () => {
        // Reset de drag state als de touch wordt geannuleerd
        setDragState({
            isActive: false,
            start: null,
            end: null,
            hasMoved: false
        });
    };

    // Memoize chartData om onnodige herberekeningen te voorkomen
    const chartData = useMemo(() => ({
        labels: scheduleData.map(d => parseISO(d.validfrom)),
        datasets: [
            ...(startOfDay(selectedDate).getTime() === startOfDay(new Date()).getTime() ? [{
                label: 'Huidige Tijd',
                data: [
                    {
                        x: currentTime.getTime() - (15 * 60 * 1000 / 2), // 15 minuten / 2 voor de offset
                        y: 0
                    },
                    {
                        x: currentTime.getTime() - (15 * 60 * 1000 / 2), // 15 minuten / 2 voor de offset
                        y: 1
                    }
                ],
                borderColor: 'rgba(255, 0, 0, 0.8)',
                borderWidth: 2,
                borderDash: [5, 5],
                pointRadius: 0,
                fill: false,
                yAxisID: 'y1',
                order: 0
            }] : []),
            {
                label: 'Geselecteerde Periodes',
                data: scheduleData.map(schedule => {
                    const currentTime = parseISO(schedule.validfrom).getTime();
                    const isSelected = selectedQuarters.some(q => {
                        return currentTime >= q.start && currentTime < q.end;
                    });
                    return {
                        x: currentTime,
                        y: isSelected ? 1 : 0
                    };
                }),
                type: 'line',
                borderColor: 'transparent',
                backgroundColor: 'rgba(255, 215, 0, 0.3)',
                fill: true,
                yAxisID: 'y1',
                order: 0,
                pointRadius: 0,
                borderWidth: 0,
                tension: 0,
                stepped: 'middle'
            },
            {
                label: getElectricityPriceLabel(),
                data: scheduleData.map(d => ({
                    x: parseISO(d.validfrom).getTime(),
                    y: getPrice(d)
                })),
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
                fill: false,
                yAxisID: 'y'
            },
            {
                label: 'Emissiefactor (g CO2/kWh)',
                data: scheduleData.map(d => ({
                    x: parseISO(d.validfrom).getTime(),
                    y: d.emissionfactor
                })),
                borderColor: 'rgb(255, 99, 132)',
                tension: 0.1,
                fill: false,
                yAxisID: 'y2',
                hidden: !showEmissionFactor,
                order: 5
            },
            {
                label: getHydrogenPriceLabel(),
                data: scheduleData.map(d => ({
                    x: parseISO(d.validfrom).getTime(),
                    y: getHydrogenPrice(d) || null
                })).filter(d => d.y !== null),
                borderColor: 'rgb(34, 197, 94)', // Groen voor waterstof
                tension: 0.1,
                fill: false,
                yAxisID: 'y4',
                hidden: !showHydrogenPrice,
                order: 6
            },
            {
                label: 'Autopilot Aan',
                data: scheduleData.map(schedule => ({
                    x: parseISO(schedule.validfrom).getTime(),
                    y: schedule.manual === null && schedule.automatic ? 1 : 0
                })),
                type: 'bar',
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                yAxisID: 'y1',
                barPercentage: 1.0,
                categoryPercentage: 1.0,
                borderWidth: 0,
                borderSkipped: false,
                borderRadius: 0,
                barThickness: 'flex',
                stack: 'status',
                borderColor: 'transparent'
            },
            {
                label: 'Forceer Aan',
                data: scheduleData.map(schedule => ({
                    x: parseISO(schedule.validfrom).getTime(),
                    y: schedule.manual === true ? 1 : null
                })),
                type: 'bar',
                backgroundColor: 'rgba(75, 192, 75, 0.5)',
                yAxisID: 'y1',
                barPercentage: 1.0,
                categoryPercentage: 1.0,
                borderWidth: 0,
                borderSkipped: false,
                borderRadius: 0,
                barThickness: 'flex',
                stack: 'status',
                borderColor: 'transparent'
            },
            {
                label: 'Blokkeer Aan',
                data: scheduleData.map(schedule => ({
                    x: parseISO(schedule.validfrom).getTime(),
                    y: schedule.manual === false ? 1 : null
                })),
                type: 'bar',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                yAxisID: 'y1',
                barPercentage: 1.0,
                categoryPercentage: 1.0,
                borderWidth: 0,
                borderSkipped: false,
                borderRadius: 0,
                barThickness: 'flex',
                stack: 'status',
                borderColor: 'transparent'
            }
        ]
    }), [scheduleData, currentTime, selectedQuarters, showEmissionFactor, showHydrogenPrice, selectedDate, electricitySettings, hydrogenDisplaySettings]);

    const options = useMemo(() => {
        const startOfSelectedDay = moment(selectedDate).tz('Europe/Amsterdam').startOf('day');
        const endOfSelectedDay = moment(selectedDate).tz('Europe/Amsterdam').endOf('day');

        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'nearest',
                axis: 'x',
                intersect: false
            },
            onClick: handleMouseUp,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        filter: function(legendItem, data) {
                            return legendItem.datasetIndex !== 0 && legendItem.datasetIndex !== 1;
                        }
                    },
                    onClick: function(e, legendItem, legend) {
                        const index = legendItem.datasetIndex;
                        const ci = legend.chart;
                        if (index === 3) {
                            setShowEmissionFactor(!showEmissionFactor);
                        } else if (index === 4) {
                            setShowHydrogenPrice(!showHydrogenPrice);
                        }
                        Chart.defaults.plugins.legend.onClick.call(this, e, legendItem, legend);
                    }
                },
                title: {
                    display: true,
                    text: t('chart.title')
                },
                tooltip: {
                    callbacks: {
                        title: (context) => {
                            // Als het de huidige tijd lijn is (eerste dataset)
                            if (context[0].datasetIndex === 0) {
                                // Gebruik de meest recente tijd
                                return moment(new Date()).tz('Europe/Amsterdam').format('DD MMMM YYYY HH:mm:ss');
                            }
                            return moment.utc(context[0].parsed.x).tz('Europe/Amsterdam').format('DD MMMM YYYY HH:mm:ss');
                        },
                        label: function(context) {
                            // Verberg de label voor "Geselecteerde periodes" (dataset index 1)
                            if (context.datasetIndex === 1) {
                                return null;
                            }
                            return context.dataset.label + ': ' + context.parsed.y;
                        }
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'hour',
                        displayFormats: {
                            hour: 'HH:mm'
                        },
                        parser: 'YYYY-MM-DDTHH:mm:ss',
                        tooltipFormat: 'DD MMMM YYYY HH:mm',
                        timezone: 'Europe/Amsterdam'
                    },
                    title: {
                        display: true,
                        text: 'Tijd (CET)'
                    },
                    adapters: {
                        date: {
                            locale: nl
                        }
                    },
                    grid: {
                        display: true,
                        drawOnChartArea: false,
                        drawTicks: true,
                        color: 'rgba(0, 0, 0, 0.1)',
                        offset: true,
                        drawBorder: true,
                        tickLength: 0,
                        ticks: {
                            source: 'auto',
                            maxRotation: 0,
                            autoSkip: false,
                            callback: function(value) {
                                const date = moment.utc(value).tz('Europe/Amsterdam');
                                return date.minutes() === 0 ? date.format('HH:mm') : '';
                            }
                        }
                    },
                    offset: true,
                    stacked: true,
                    barPercentage: 1.0,
                    categoryPercentage: 1.0,
                    ticks: {
                        callback: function(value) {
                            return moment.utc(value).tz('Europe/Amsterdam').format('HH:mm');
                        }
                    },
                    min: startOfSelectedDay.valueOf(),
                    max: endOfSelectedDay.valueOf(),
                    distribution: 'linear',
                    bounds: 'ticks'
                },
                y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                        display: true,
                        text: 'Prijs (€/kWh)'
                    },
                    grid: {
                        color: (context) => {
                            if (context.tick.value === 0) {
                                return 'rgba(0, 0, 0, 0.5)';
                            }
                            return 'rgba(0, 0, 0, 0.1)';
                        },
                        lineWidth: (context) => {
                            if (context.tick.value === 0) {
                                return 2;
                            }
                            return 1;
                        }
                    },
                    ticks: {
                        callback: (value) => `${value.toFixed(2)} €`
                    }
                },
                y1: {
                    type: 'linear',
                    display: false,
                    position: 'right',
                    min: 0,
                    max: 1,
                    title: {
                        display: true,
                        text: 'Status'
                    },
                    grid: {
                        drawOnChartArea: false
                    },
                    ticks: {
                        stepSize: 1,
                        callback: (value) => {
                            if (value === 0) return 'Uit';
                            if (value === 1) return 'Aan';
                            return '';
                        }
                    }
                },
                y2: {
                    type: 'linear',
                    display: showEmissionFactor,
                    position: 'right',
                    title: {
                        display: true,
                        text: 'Emissiefactor (g CO2/kWh)'
                    },
                    grid: {
                        drawOnChartArea: false
                    },
                    ticks: {
                        callback: (value) => `${value.toFixed(0)} g`
                    }
                },
                y3: {
                    type: 'linear',
                    display: false,
                    position: 'right',
                    min: 0,
                    max: 1,
                    grid: {
                        drawOnChartArea: false
                    }
                },
                y4: {
                    type: 'linear',
                    display: showHydrogenPrice,
                    position: 'right',
                    min: 0,
                    title: {
                        display: true,
                        text: 'Waterstofprijs (€/kg)'
                    },
                    grid: {
                        drawOnChartArea: false
                    },
                    ticks: {
                        callback: (value) => `${value.toFixed(2)} €`
                    }
                }
            }
        };
    }, [t, showEmissionFactor, showHydrogenPrice, selectedDate]);

    // Functie om de geselecteerde kwartieren te groeperen
    const groupSelectedQuarters = () => {
        if (selectedQuarters.length === 0) return [];

        // Sorteer de kwartieren op starttijd
        const sortedQuarters = [...selectedQuarters].sort((a, b) => a.start - b.start);
        const groups = [];
        let currentGroup = [sortedQuarters[0]];

        for (let i = 1; i < sortedQuarters.length; i++) {
            const current = sortedQuarters[i];
            const previous = currentGroup[currentGroup.length - 1];

            // Als het huidige kwartier direct aansluit op het vorige, voeg toe aan huidige groep
            if (current.start === previous.end) {
                currentGroup.push(current);
            } else {
                // Anders, sluit huidige groep af en start nieuwe
                groups.push(currentGroup);
                currentGroup = [current];
            }
        }

        // Voeg laatste groep toe
        if (currentGroup.length > 0) {
            groups.push(currentGroup);
        }

        return groups;
    };

    const handleGroupToggle = async (group, checked) => {
        try {
            const blocks = group.map(quarter => {
                // Converteer de geselecteerde CET tijden naar UTC voor de API
                const utcStart = moment(quarter.start).tz('Europe/Amsterdam').utc();
                const utcEnd = moment(quarter.end).tz('Europe/Amsterdam').utc();
                
                return {
                    validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                    validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                    state: checked
                };
            });

            await post('/schedule', { 
                blocks: blocks,
                allowBackward: true 
            });
            
            // Herlaad de data met UTC conversie
            const cetStart = UTC2CET(selectedDate);
            const refreshValidFrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
            const refreshValidTo = format(CET2UTC(cetStart), "yyyy-MM-dd'T'22:00:00");
            const scheduleResponse = await get(`/schedule?validfrom=${refreshValidFrom}&validto=${refreshValidTo}`);
            
            if (scheduleResponse.data && scheduleResponse.data.blocks) {
                // Converteer alle timestamps naar CET
                const convertedBlocks = scheduleResponse.data.blocks.map(block => ({
                    ...block,
                    validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                    validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                }));
                setScheduleData(convertedBlocks);
            }
            
            // Reset selectie
            setSelectedQuarters([]);
        } catch (error) {
            console.error('Error updating schedule:', error);
        }
    };

    const handleApplySelection = async () => {
        if (selectedQuarters.length === 0) return;

        try {
            const blocks = selectedQuarters.map(quarter => {
                // Converteer de geselecteerde CET tijden naar UTC voor de API
                const utcStart = moment(quarter.start).tz('Europe/Amsterdam').utc();
                const utcEnd = moment(quarter.end).tz('Europe/Amsterdam').utc();
                
                let stateValue;
                if (selectionMode === 'manual_on') {
                    stateValue = true; // Forceer Aan
                } else if (selectionMode === 'manual_off') {
                    stateValue = false; // Blokkeer Aan
                } else {
                    stateValue = null; // Override verwijderen
                }

                return {
                    validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                    validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                    state: stateValue
                };
            });

            if (selectionMode === 'remove') {
                await post('/schedule?delete=true', { 
                    blocks: blocks,
                    allowBackward: true 
                });
            } else {
                await post('/schedule', { 
                    blocks: blocks,
                    allowBackward: true 
                });
            }
            
            // Herlaad de data met UTC conversie
            const cetStart = UTC2CET(selectedDate);
            const refreshValidFrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
            const refreshValidTo = format(CET2UTC(cetStart), "yyyy-MM-dd'T'22:00:00");
            const scheduleResponse = await get(`/schedule?validfrom=${refreshValidFrom}&validto=${refreshValidTo}`);
            
            if (scheduleResponse.data && scheduleResponse.data.blocks) {
                // Converteer alle timestamps naar CET
                const convertedBlocks = scheduleResponse.data.blocks.map(block => ({
                    ...block,
                    validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                    validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                }));
                setScheduleData(convertedBlocks);
            }
            
            // Reset selectie en schakel selectiemodus uit
            setSelectedQuarters([]);
            setShowModeOptions(false);
            setSelectionMode(null);
        } catch (error) {
            console.error('Error updating schedule:', error);
        }
    };

    const handleDateClick = (event) => {
        setDatePickerAnchor(event.currentTarget);
    };

    const handleDatePickerClose = () => {
        setDatePickerAnchor(null);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
                <Typography color={error.type} variant="h6">
                    {error.message}
                </Typography>
                {error.details && (
                    <Typography color="text.secondary" variant="body2">
                        {error.details}
                    </Typography>
                )}
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => {
                        setError(null);
                        // Herlaad de data
                        const cetStart = UTC2CET(selectedDate);
                        const refreshValidFrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
                        const refreshValidTo = format(CET2UTC(cetStart), "yyyy-MM-dd'T'22:00:00");
                        get(`/schedule?validfrom=${refreshValidFrom}&validto=${refreshValidTo}`)
                            .then(response => {
                                if (response.data?.blocks?.length > 0) {
                                    const convertedBlocks = response.data.blocks.map(block => ({
                                        ...block,
                                        validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                                        validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                                    }));
                                    setScheduleData(convertedBlocks);
                                } else {
                                    setScheduleData([]);
                                }
                            })
                            .catch(handleError);
                    }}
                >
                    {t('errors.retry')}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <IconButton 
                    onClick={handlePreviousDay} 
                    color="primary"
                    disabled={selectedDate.getFullYear() <= 2024}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        mx: 2, 
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1
                        },
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        transition: 'background-color 0.2s ease'
                    }}
                    onClick={handleDateClick}
                >
                    {moment(selectedDate).tz('Europe/Amsterdam').format('dddd D MMMM YYYY')}
                </Typography>
                <IconButton 
                    onClick={handleNextDay} 
                    color="primary"
                    disabled={selectedDate >= addDays(new Date(), 1)}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            {/* Date Picker Popover */}
            <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                    <DateCalendar
                        value={selectedDate}
                        onChange={(newDate) => {
                            setSelectedDate(newDate);
                            handleDatePickerClose();
                        }}
                        sx={{ 
                            '& .MuiDateCalendar-root': {
                                width: 'auto',
                                height: 'auto'
                            }
                        }}
                    />
                </LocalizationProvider>
            </Popover>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {!showModeOptions ? (
                            <Button
                                variant="outlined"
                                onClick={() => setShowModeOptions(true)}
                                startIcon={<EditIcon />}
                            >
                                Selectie modus
                            </Button>
                        ) : (
                            <>
                                <ToggleButtonGroup
                                    value={selectionMode}
                                    exclusive
                                    onChange={(e, newMode) => {
                                        if (newMode !== null) { // Alleen updaten als er een nieuwe modus is geselecteerd
                                            setSelectionMode(newMode);
                                        }
                                    }}
                                    aria-label="selectie modus"
                                    size="small"
                                >
                                    <ToggleButton value="manual_on" aria-label="Forceer Aan">
                                        Forceer Aan
                                    </ToggleButton>
                                    <ToggleButton value="manual_off" aria-label="Blokkeer Aan">
                                        Blokkeer Aan
                                    </ToggleButton>
                                    <ToggleButton value="remove" aria-label="override verwijderen">
                                        Override Verwijderen
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <IconButton 
                                    onClick={() => {
                                        setShowModeOptions(false);
                                        setSelectionMode(null);
                                        setSelectedQuarters([]);
                                    }}
                                    color="primary"
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </>
                        )}



                        {selectedQuarters.length > 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleApplySelection}
                                size="small"
                            >
                                Toepassen ({selectedQuarters.length} kwartierblok{selectedQuarters.length !== 1 ? 'ken' : 'k'})
                            </Button>
                        )}
                    </Box>

                    <Box 
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                        sx={{ 
                            height: '600px', 
                            width: '100%', 
                            position: 'relative',
                            cursor: showModeOptions ? 'crosshair' : 'default',
                            backgroundColor: showModeOptions ? 'rgba(255, 255, 0, 0.05)' : 'transparent',
                            transition: 'background-color 0.2s ease',
                            touchAction: 'none' // Voorkomt standaard touch acties zoals scrollen
                        }}
                    >
                        <Line ref={chartRef} data={chartData} options={options} />
                        {dragState.isActive && (
                            <Box
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onTouchCancel={handleTouchCancel}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    cursor: 'crosshair',
                                    zIndex: 1,
                                    touchAction: 'none' // Voorkomt standaard touch acties zoals scrollen
                                }}
                            />
                        )}
                        {dragState.isActive && dragState.start && dragState.end && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: Math.min(dragState.start.y, dragState.end.y),
                                    left: Math.min(dragState.start.x, dragState.end.x),
                                    width: Math.abs(dragState.end.x - dragState.start.x),
                                    height: Math.abs(dragState.end.y - dragState.start.y),
                                    border: '1px dashed #000',
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    pointerEvents: 'none',
                                    zIndex: 2,
                                    animation: 'dash 1s linear infinite',
                                    '@keyframes dash': {
                                        '0%': {
                                            backgroundPosition: '0 0'
                                        },
                                        '100%': {
                                            backgroundPosition: '20px 0'
                                        }
                                    }
                                }}
                            />
                        )}

                        {selectedQuarters.length > 0 && (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: 2, 
                                mb: 3,
                                position: 'relative',
                                height: '40px'
                            }}>
                                {groupSelectedQuarters().map((group, index) => {
                                    const startTime = parseISO(group[0].start);
                                    const endTime = parseISO(group[group.length - 1].end);
                                    const isOn = group.every(quarter => {
                                        const schedule = scheduleData.find(s => 
                                            parseISO(s.validfrom).getTime() === quarter.start && 
                                            parseISO(s.validto).getTime() === quarter.end
                                        );
                                        return schedule && schedule.manual === true;
                                    });

                                    return (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Switch
                                                    checked={isOn}
                                                    onChange={(e) => handleGroupToggle(group, e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={`${moment(startTime).tz('Europe/Amsterdam').format('HH:mm')} - ${moment(endTime).tz('Europe/Amsterdam').format('HH:mm')}`}
                                            sx={{
                                                position: 'absolute',
                                                left: `${(startTime.getTime() - parseISO(scheduleData[0].validfrom).getTime()) / (parseISO(scheduleData[scheduleData.length - 1].validto).getTime() - parseISO(scheduleData[0].validfrom).getTime()) * 100}%`,
                                                transform: 'translateX(-50%)'
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
            
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Aan-periodes
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>Eind</TableCell>
                                    <TableCell>Duur</TableCell>
                                    <TableCell align="right">Verbruik (kWh)</TableCell>
                                    <TableCell align="right">Gem. prijs (€/kWh)</TableCell>
                                    {showEmissionFactor && (
                                        <>
                                            <TableCell align="right">Emissiefactor (g/kWh)</TableCell>
                                            <TableCell align="right">Emissie (kg)</TableCell>
                                        </>
                                    )}
                                    <TableCell align="right">Kosten (€)</TableCell>
                                    <TableCell align="right">Waterstof (kg)</TableCell>
                                    {showHydrogenPrice && (
                                        <>
                                            <TableCell align="right">H₂ prijs (€/kg)</TableCell>
                                            <TableCell align="right">H₂ waarde (€)</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {onPeriods.map((period, index) => {
                                    const hours = Math.floor(period.hours);
                                    const minutes = period.duration % 60;
                                    const durationText = `${hours} uur${minutes > 0 ? ` en ${minutes} minuten` : ''}`;
                                    const totalEmission = (period.totalConsumption * period.averageEmissionFactor) / 1000; // Omrekenen naar kg
                                    
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Typography color={period.status === 'Override actief' ? 'success.main' : 'primary.main'}>
                                                    {period.status}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{moment(period.start).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>{moment(period.end).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>{durationText}</TableCell>
                                            <TableCell align="right">{period.totalConsumption.toFixed(1)}</TableCell>
                                            <TableCell align="right">{period.averagePrice.toFixed(2)}</TableCell>
                                            {showEmissionFactor && (
                                                <>
                                                    <TableCell align="right">{period.averageEmissionFactor.toFixed(0)}</TableCell>
                                                    <TableCell align="right" sx={{ color: 'error.main' }}>{totalEmission.toFixed(2)}</TableCell>
                                                </>
                                            )}
                                            <TableCell align="right">{period.totalCost.toFixed(2)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                {period.hydrogenProduced.toFixed(2)}
                                            </TableCell>
                                            {showHydrogenPrice && (
                                                <>
                                                    <TableCell align="right" sx={{ color: 'success.main' }}>
                                                        {period.averageHydrogenPrice > 0 ? period.averageHydrogenPrice.toFixed(2) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                        {period.hydrogenCost > 0 ? period.hydrogenCost.toFixed(2) : '-'}
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Totalen van de dag
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" scope="row">Totaal draaitijd</TableCell>
                                    <TableCell align="right">
                                        {(() => {
                                            const totalMinutes = onPeriods.reduce((sum, period) => sum + period.duration, 0);
                                            const hours = Math.floor(totalMinutes / 60);
                                            const minutes = totalMinutes % 60;
                                            return `${hours} uur${minutes > 0 ? ` en ${minutes} minuten` : ''}`;
                                        })()}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">Totaal verbruik</TableCell>
                                    <TableCell align="right">
                                        {onPeriods.reduce((sum, period) => sum + period.totalConsumption, 0).toFixed(1)} kWh
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">Gemiddelde prijs</TableCell>
                                    <TableCell align="right">
                                        {(() => {
                                            const totalCost = onPeriods.reduce((sum, period) => sum + period.totalCost, 0);
                                            const totalConsumption = onPeriods.reduce((sum, period) => sum + period.totalConsumption, 0);
                                            return `${(totalCost / totalConsumption).toFixed(2)} €/kWh`;
                                        })()}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">Totale kosten</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {onPeriods.reduce((sum, period) => sum + period.totalCost, 0).toFixed(2)} €
                                    </TableCell>
                                </TableRow>
                                {showEmissionFactor && (
                                    <>
                                        <TableRow>
                                            <TableCell component="th" scope="row">Totale emissie</TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                                {(() => {
                                                    const totalEmission = onPeriods.reduce((sum, period) => 
                                                        sum + (period.totalConsumption * period.averageEmissionFactor), 0) / 1000; // kg CO₂
                                                    return `${totalEmission.toFixed(2)} kg CO₂`;
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">Gemiddelde emissiefactor</TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main' }}>
                                                {(() => {
                                                    const totalEmission = onPeriods.reduce((sum, period) => 
                                                        sum + (period.totalConsumption * period.averageEmissionFactor), 0);
                                                    const totalConsumption = onPeriods.reduce((sum, period) => 
                                                        sum + period.totalConsumption, 0);
                                                    return `${(totalEmission / totalConsumption).toFixed(0)} g CO₂/kWh`;
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                                <TableRow>
                                    <TableCell component="th" scope="row">Totaal waterstof geproduceerd</TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                        {onPeriods.reduce((sum, period) => sum + period.hydrogenProduced, 0).toFixed(2)} kg
                                    </TableCell>
                                </TableRow>
                                {showEmissionFactor && (
                                    <TableRow>
                                        <TableCell component="th" scope="row">CO₂ per kg waterstof</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>
                                            {(() => {
                                                const totalEmission = onPeriods.reduce((sum, period) => 
                                                    sum + (period.totalConsumption * period.averageEmissionFactor), 0) / 1000; // kg CO₂
                                                const totalHydrogen = onPeriods.reduce((sum, period) => 
                                                    sum + period.hydrogenProduced, 0);
                                                return `${(totalEmission / totalHydrogen).toFixed(2)} kg CO₂/kg H₂`;
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {showHydrogenPrice && (
                                    <>
                                        <TableRow>
                                            <TableCell component="th" scope="row">Gemiddelde waterstofprijs</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                                {(() => {
                                                    const totalHydrogenCost = onPeriods.reduce((sum, period) => 
                                                        sum + period.hydrogenCost, 0);
                                                    const totalHydrogen = onPeriods.reduce((sum, period) => 
                                                        sum + period.hydrogenProduced, 0);
                                                    return totalHydrogen > 0 ? `${(totalHydrogenCost / totalHydrogen).toFixed(2)} €/kg` : '-';
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" scope="row">Totale waterstofwaarde</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                {onPeriods.reduce((sum, period) => sum + period.hydrogenCost, 0).toFixed(2)} €
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default Planning; 